// src/middleware/axios-auth.ts
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable no-param-reassign */
import axios, { AxiosHeaders, type InternalAxiosRequestConfig } from 'axios';
import store from '@/store';
import Router from '@/router';
import utils from '@/services/utils';

export type ExtAxiosConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

const getPath = (url?: string) => {
  const cleanUrl = (url || '').split('?')[0];
  return cleanUrl.split('/').splice(3).join('/');
};

const shouldSkipAccessToken = (url?: string) => {
  const path = getPath(url);

  return path === 'auth/login/agent' || path === 'auth/refresh' || path === 'auth/sso/verify-ticket';
};

const shouldForceRemoveAuthorization = (url?: string) => {
  const path = getPath(url);

  return path === 'auth/login/agent' || path === 'auth/sso/verify-ticket';
};

const ensureHeaders = (req: InternalAxiosRequestConfig) => {
  if (!(req.headers instanceof AxiosHeaders)) {
    req.headers = AxiosHeaders.from(req.headers || {});
  }
  return req.headers;
};

export const applyAuthRequest = (request: InternalAxiosRequestConfig) => {
  const req = request as ExtAxiosConfig;
  const headers = ensureHeaders(req);

  if (shouldForceRemoveAuthorization(req.url as any)) {
    headers.delete('Authorization');
    return req;
  }

  if (shouldSkipAccessToken(req.url as any)) {
    return req;
  }

  const accessToken = utils._session.get('ambgames-user-access-token');
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  return req;
};

export const createAuthErrorHandler = () => {
  let isRefreshing = false;
  let refreshPromise: Promise<void> | null = null;

  return async (err: any) => {
    const originalConfig = (err?.config || {}) as ExtAxiosConfig;

    if (!err || !err.response) {
      return Promise.reject(err);
    }

    ensureHeaders(originalConfig);

    const path = getPath(originalConfig.url as any);

    // refresh endpoint broken -> logout
    if (err.response.status === 500 && path === 'auth/refresh') {
      await store.dispatch('Logout');
      try {
        Router.replace('/login');
      } catch (_) {
        Router.push('/login');
      }
      return Promise.reject(err);
    }

    if (path === 'auth/login/agent' || path === 'auth/sso/verify-ticket') {
      return Promise.reject(err);
    }

    if (path !== 'auth/refresh' && err.response.status === 401 && !originalConfig._retry) {
      originalConfig._retry = true;

      try {
        if (isRefreshing && refreshPromise) {
          await refreshPromise;
        } else {
          isRefreshing = true;

          refreshPromise = (async () => {
            try {
              await store.dispatch('RefreshToken');
            } finally {
              isRefreshing = false;
              refreshPromise = null;
            }
          })();

          await refreshPromise;
        }

        const accessToken = utils._session.get('ambgames-user-access-token');

        if (accessToken) {
          ensureHeaders(originalConfig).set('Authorization', `Bearer ${accessToken}`);
        }

        return axios(originalConfig as any);
      } catch (_error) {
        return Promise.reject(_error);
      }
    }

    return Promise.reject(err);
  };
};
